import { createSSRApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App'
import pinia from './stores/index'
import i18n, { getCurrentLanguage, t as translate } from './i18n/index.js' // 国际化
import { handleError, withErrorHandling } from './utils/errorHandler.js'
import { disableConsoleInProduction } from './utils/logger.js'

// 是否开启深色模式总开关（false 表示禁用，不读取本地缓存，固定浅色）
const ENABLE_DARK_MODE = false;
let darkMode = ENABLE_DARK_MODE ? (uni.getStorageSync('isDarkMode') || false) : false;

function initDarkModeStyle(isDark) {
  // #ifdef H5
  // 仅在 H5 / 浏览器环境操作 DOM document
  if (typeof document !== 'undefined' && document.documentElement) {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark-mode');
    } else {
      root.classList.remove('dark-mode');
    }
  }
  // #endif
  // #ifndef H5
  // App 原生/小程序环境：无浏览器 DOM 对象
  // #endif
}

// #ifdef H5
initDarkModeStyle(darkMode);
// #endif

// #ifdef H5
// 尽早注册阿里云播放器错误拦截（必须在 createApp 之前，防止 SDK 初始化时的 DOM 访问错误）
if (typeof window !== 'undefined') {
	window.addEventListener('error', function(event) {
		const error = event.error;
		const message = event.message || (error && error.message);
		const filename = event.filename || (error && error.fileName);
		
		// 拦截所有 aliplayer 相关错误
		if (filename && (filename.includes('aliplayer') || filename.includes('AliPlayer'))) {
			console.warn('[AliPlayer Error] 已拦截:', message, 'file:', filename);
			event.preventDefault();
			event.stopPropagation();
			return true;
		}
		
		// 拦截百度统计 unload 权限策略警告
		if (filename && filename.includes('hm.js') && message && message.includes('unload')) {
			event.preventDefault();
			event.stopPropagation();
			return true;
		}

		// 拦截第三方性能监控 startTime 错误（Chrome 扩展注入）
		if (message && message.includes('Cannot read properties of undefined') && message.includes('startTime')) {
			event.preventDefault();
			event.stopPropagation();
			return true;
		}
		
		// 拦截 DOM 访问错误（innerHTML/style 等）
		if (message) {
			if ((message.includes('Cannot set properties of null') || message.includes('Cannot read properties of null'))
				&& (message.includes('innerHTML') || message.includes('style') || message.includes('textContent') || message.includes('appendChild') || message.includes('paused')))
			{
				console.warn('[AliPlayer DOM Error] 已拦截:', message);
				event.preventDefault();
				event.stopPropagation();
				return true;
			}
			if (message.includes('Cannot read properties of null') &&
				(message.includes('style') || message.includes('innerHTML') || message.includes('offsetWidth') || message.includes('paused')))
			{
				console.warn('[AliPlayer Style Error] 已拦截:', message);
				event.preventDefault();
				event.stopPropagation();
				return true;
			}
			if (message.includes('403') &&
				message.includes('Forbidden') &&
				(message.includes('vod.slash99.com') || message.includes('aliplayer'))) {
				console.warn('Aliyun player video 403 error (ignored):', message);
				event.preventDefault();
				event.stopPropagation();
				return;
			}
		}
	}, true);

	// 拦截 Permissions Policy 违规警告（百度统计 unload 等）
	window.addEventListener('violations', function(event) {
		if (event && event.violatedDirective && event.violatedDirective.indexOf('unload') !== -1) {
			event.preventDefault();
			event.stopPropagation();
			return true;
		}
	}, true);

	window.addEventListener('unhandledrejection', function(event) {
		const reason = event.reason;
		if (reason && reason.message) {
			// 第三方性能监控 startTime 错误（Chrome 扩展注入）
			if (reason.message.includes('Cannot read properties of undefined') && reason.message.includes('startTime')) {
				event.preventDefault();
				return;
			}
			// 视频元素被移除时的 play() 中断，属于浏览器正常行为
			if (reason.name === 'AbortError' || reason.message.includes('media was removed')) {
				event.preventDefault();
				return;
			}
			if (reason.message.includes('Cannot set properties of null') &&
				reason.message.includes('innerHTML')) {
				console.warn('Aliyun player container rejection (ignored):', reason.message);
				event.preventDefault();
				return;
			}
			if (reason.message.includes('Cannot read properties of null') &&
				(reason.message.includes('style') || reason.message.includes('paused'))) {
				console.warn('Aliyun player DOM property rejection (ignored):', reason.message);
				event.preventDefault();
				return;
			}
		}
	});
}
// #endif

export function createApp() {
	// 生产环境禁用 console.log
	disableConsoleInProduction()
	
	// #ifdef H5
	// 阿里云播放器错误拦截已在模块顶层注册，此处无需重复添加
	// 百度统计 SPA 路由追踪：拦截 uni 导航方法，每次页面跳转通知百度统计
	if (typeof uni !== 'undefined') {
		const _trackBaiduPageView = () => {
			if (typeof window !== 'undefined' && typeof _hmt !== 'undefined') {
				setTimeout(() => {
					const pages = getCurrentPages()
					if (pages.length > 0) {
						const route = '/' + (pages[pages.length - 1].route || '')
						_hmt.push(['_trackPageview', route])
					}
				}, 50)
			}
		}
		const _navMethods = ['navigateTo', 'redirectTo', 'reLaunch', 'switchTab']
		_navMethods.forEach(method => {
			const original = uni[method]
			if (typeof original === 'function') {
				uni[method] = function(opts) {
					const result = original.call(this, opts)
					_trackBaiduPageView()
					return result
				}
			}
		})
		const originalBack = uni.navigateBack
		if (typeof originalBack === 'function') {
			uni.navigateBack = function(opts) {
				const result = originalBack.call(this, opts)
				_trackBaiduPageView()
				return result
			}
		}
		// 浏览器前进/后退也追踪
		window.addEventListener('popstate', () => {
			_trackBaiduPageView()
		})
	}
	// #endif
	
  const app = createSSRApp(App)
  app.use(i18n)
  // 使用 Pinia 状态管理
  app.use(pinia)

  app.config.globalProperties.$darkMode = darkMode;
  app.config.globalProperties.$toggleDarkMode = function(isDark) {
    darkMode = isDark;
    uni.setStorageSync('isDarkMode', isDark);
    initDarkModeStyle(isDark);
    app.config.globalProperties.$darkMode = isDark;
  };
  app.config.globalProperties.t = function(key, params) {
    return i18n.global.t(key, params);
  };
  
  // 全局错误处理方法
  app.config.globalProperties.$handleError = handleError;
  app.config.globalProperties.$withErrorHandling = withErrorHandling;

  return {
    app
  }
}
